import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useCategoryDetails } from "~/api/swr/useCategoryDetails";
import { useCategories } from "~/api/swr/useCategories";
import {
  TitleAndDescriptionForm,
  type FormValues,
} from "~/molecules/TitleAndDescriptionForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/atoms/dialog";
import {
  createCategory,
  updateCategory,
  deleteCategory,
} from "~/api/v2/requests";
import { DeleteCategoryStep } from "./DeleteCategoryStep";

interface Props {
  categoryId?: number;
  trigger: React.ReactNode;
  onClosePopover?: () => void;
  onClose?: () => void;
}

export const CategoryModal = ({
  categoryId,
  trigger,
  onClosePopover,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [isDeleteStep, setIsDeleteStep] = useState(false);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setIsDeleteStep(false);
      onClose?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className={isDeleteStep ? "sm:max-w-3xl" : undefined}>
        <DialogHeader>
          <DialogTitle>{t(($) => $.commons.document.category)}</DialogTitle>
        </DialogHeader>
        {!categoryId ? (
          <CreateCategory close={() => onClosePopover?.()} />
        ) : isDeleteStep ? (
          <DeleteCategoryStep
            categoryId={categoryId}
            onBack={() => setIsDeleteStep(false)}
            onDeleted={() => handleOpenChange(false)}
          />
        ) : (
          <EditCategory
            categoryId={categoryId}
            close={() => handleOpenChange(false)}
            onDeleteWithDocuments={() => setIsDeleteStep(true)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

interface EditCategoryProps {
  categoryId: number;
  close: () => void;
  onDeleteWithDocuments: () => void;
}

const EditCategory = ({
  categoryId,
  close,
  onDeleteWithDocuments,
}: EditCategoryProps) => {
  const { t } = useTranslation();
  const { data: category, mutate } = useCategoryDetails(categoryId, true);
  const { mutate: mutateAll } = useCategories();
  const [isDeleting, setIsDeleting] = useState(false);

  const hasDocuments = category?.hasDocuments ?? false;

  const removeHandler = async () => {
    try {
      setIsDeleting(true);
      await deleteCategory(categoryId);
      void mutateAll();
      toast.success(t(($) => $.toasts.success.categoryRemoved));
      close();
    } catch {
      toast.error(t(($) => $.toasts.error.categoryNotRemoved));
    } finally {
      setIsDeleting(false);
    }
  };

  const onSubmit = async (values: FormValues) => {
    try {
      await updateCategory(categoryId, values);
      void mutate();
      void mutateAll();
      toast.success(t(($) => $.toasts.success.categoryUpdated));
      close();
    } catch {
      toast.error(t(($) => $.toasts.error.generic));
    }
  };

  return (
    <TitleAndDescriptionForm
      data={category}
      onDelete={hasDocuments ? onDeleteWithDocuments : removeHandler}
      onSubmit={onSubmit}
      deleteDisabled={!category || isDeleting}
      confirmDelete={!hasDocuments}
    />
  );
};

interface CreateCategoryProps {
  close: () => void;
}

const CreateCategory = ({ close }: CreateCategoryProps) => {
  const { t } = useTranslation();
  const { mutate: mutateAllCategories } = useCategories();

  const onSubmit = async (values: FormValues) => {
    try {
      await createCategory(values);
      void mutateAllCategories();
      toast.success(t(($) => $.toasts.success.categoryCreated));
      close();
    } catch {
      toast.error(t(($) => $.toasts.error.categoryNotCreated));
    }
  };

  return <TitleAndDescriptionForm onSubmit={onSubmit} />;
};
